import React from 'react';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Box } from 'src/design/atoms/Box';
import { Card } from 'src/design/atoms/Card';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';
import { TextInput } from 'src/design/atoms/TextInput';
import CurrencyInput from 'react-currency-input-field';
import {
  DEFAULT_DESIGNER_RATE_LIST,
  DEFAULT_LABOR_RATE_LIST,
} from './defaults';

import { RateSheetDto } from 'src/api/FiretrolApiClient';
import { Currency } from 'src/design/atoms/Currency';

interface CustomCellProps {
  value: string;
  editing: boolean;
  onChangeField: string;
  isStatic?: boolean;
  onChange?: (id: string, field: string, value: string) => void;
  onChangeId?: string;
}

function isStringNumber(str: string) {
  return !isNaN(Number(str));
}

const CustomCell: React.FC<CustomCellProps> = ({
  value,
  editing,
  onChangeId,
  onChange,
  onChangeField,
  isStatic,
}) => {
  const handleFocus = (event: any) => event.target.select();
  if (!isStatic && editing && onChange && onChangeId) {
    return (
      <Table.Td style={{ border: '1px solid black' }} className="editing-cell">
        <CurrencyInput
          onFocus={handleFocus}
          id={onChangeId}
          name={onChangeId}
          defaultValue={value}
          style={{ width: '100%', textAlign: 'right' }}
          decimalsLimit={2}
          prefix="$"
          onValueChange={(v: string, event: any) => {
            if (isStringNumber(v)) {
              onChange(onChangeId, onChangeField, v);
            }
          }}
        />
      </Table.Td>
    );
  }

  return (
    <Table.Td style={{ border: '1px solid black' }}>
      {isStringNumber(value) ? (
        <Currency precise value={Number(value)} />
      ) : (
        value
      )}
    </Table.Td>
  );
};

interface RateCardProps extends RateSheetDto {
  onRateSheetSaved: (rateSheet: RateSheetDto) => void;
}

const RateCard: React.FC<RateCardProps> = ({
  id,
  title,
  rows,
  onRateSheetSaved,
}) => {
  const [editing, setEditing] = React.useState(false);
  const [rowValues, setRowValues] = React.useState(rows);
  const [titleValue, setTitleValue] = React.useState(title);

  const onFieldChange = (id: string, field: string, value: string) => {
    setRowValues((prevState) => {
      return prevState?.map((row) => {
        if (row.id === id) {
          return {
            ...row,
            [field]: value,
          };
        } else {
          return row;
        }
      });
    });
  };

  const saveRates = () => {
    onRateSheetSaved({
      id,
      title: titleValue,
      rows: rowValues,
    });
    setEditing(false);
  };

  const cancelEdit = () => {
    setRowValues(rows);
    setTitleValue(title);
    setEditing(false);
  };

  return (
    <Card>
      <Table.ScrollContainer minWidth={650}>
        <Group justify="space-between" mb="sm">
          <Box style={{ flex: 1 }} />
          <Box style={{ flex: 1 }} ta="center">
            {editing ? (
              <TextInput
                value={titleValue}
                onChange={(e) => setTitleValue(e.currentTarget.value)}
              />
            ) : (
              <Text fw={700} size="lg" ta="center">
                {titleValue}
              </Text>
            )}
          </Box>
          <Group style={{ flex: 1 }} justify="flex-end" gap="xs">
            {editing ? (
              <>
                <BasicButton variant="subtle" onClick={cancelEdit}>
                  Cancel
                </BasicButton>
                <BasicButton onClick={saveRates}>Save Rates</BasicButton>
              </>
            ) : (
              <BasicButton variant="subtle" onClick={() => setEditing(true)}>
                Edit Rates
              </BasicButton>
            )}
          </Group>
        </Group>

        <Table
          style={{ minWidth: 650, borderCollapse: 'collapse' }}
          className="rate-table"
        >
          <Table.Thead>
            <Table.Tr>
              <CustomCell
                isStatic
                onChangeField="description"
                editing={false}
                value="Description"
              />
              <CustomCell
                isStatic
                onChangeField="baseRate"
                editing={false}
                value="Base"
              />
              <CustomCell
                isStatic
                onChangeField="timeAndAHalfRate"
                editing={false}
                value="1.5x"
              />
              <CustomCell
                isStatic
                onChangeField="doubleRate"
                editing={false}
                value="2x"
              />
              <CustomCell
                isStatic
                onChangeField="otherRate"
                editing={false}
                value="Other"
              />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows?.map((row) => {
              return (
                <Table.Tr
                  key={row.id}
                  style={{
                    backgroundColor: row.color,
                  }}
                >
                  <CustomCell
                    onChange={onFieldChange}
                    onChangeId={row.id}
                    isStatic
                    onChangeField="description"
                    editing={editing}
                    value={row.description || ''}
                  />
                  <CustomCell
                    onChange={onFieldChange}
                    onChangeId={row.id}
                    onChangeField="baseRate"
                    editing={editing}
                    value={row.baseRate || ''}
                  />
                  <CustomCell
                    onChange={onFieldChange}
                    onChangeId={row.id}
                    onChangeField="timeAndAHalfRate"
                    editing={editing}
                    value={row.timeAndAHalfRate || ''}
                  />
                  <CustomCell
                    onChange={onFieldChange}
                    onChangeId={row.id}
                    onChangeField="doubleRate"
                    editing={editing}
                    value={row.doubleRate || ''}
                  />
                  <CustomCell
                    onChange={onFieldChange}
                    onChangeId={row.id}
                    onChangeField="otherRate"
                    editing={editing}
                    value={row.otherRate || ''}
                  />
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
};

interface LaborRatesProps {
  rateType: 'design' | 'labor';
  initialRateSheets: RateSheetDto[];
  onChange: (sheets: RateSheetDto[]) => void;
}

export const LaborRates: React.FC<LaborRatesProps> = ({
  rateType,
  initialRateSheets,
  onChange,
}) => {
  const isDesign = rateType === 'design';
  const [rateSheets, setRateSheets] =
    React.useState<RateSheetDto[]>(initialRateSheets);
  React.useEffect(() => {
    onChange(rateSheets);
  }, [rateSheets]);
  const generateNewSheet = () => {
    const defaultSheet = {
      id: '1234',
      title: isDesign ? 'Designer' : 'Foreman',
      rows: isDesign ? DEFAULT_DESIGNER_RATE_LIST : DEFAULT_LABOR_RATE_LIST,
    };
    setRateSheets((prevState) => [...prevState, defaultSheet]);
  };

  const onRateSheetSaved = (rateSheet: RateSheetDto) => {
    setRateSheets((prevState) => {
      return prevState.map((sheet) => {
        if (sheet.id === rateSheet.id) {
          return rateSheet;
        } else {
          return sheet;
        }
      });
    });
  };

  const renderRateSheets = () => {
    return rateSheets.map((sheet) => {
      return (
        <RateCard
          key={sheet.id}
          rows={sheet.rows}
          title={sheet.title}
          id={sheet.id}
          onRateSheetSaved={onRateSheetSaved}
        />
      );
    });
  };

  return (
    <Stack gap="md">
      <Box>
        <BasicButton onClick={generateNewSheet}>Add Sheet</BasicButton>
      </Box>
      {renderRateSheets()}
    </Stack>
  );
};
